import { useState, useRef, useEffect } from 'react';
import { theme } from '../../../styles/theme';
import { useIsMobile } from '../../../hooks/useIsMobile';
import connieGlobe from '../../../assets/connie-globe.png';
import connieSuitcase from '../../../assets/connie-suitcase-no-bckgn.png';
import type { FormData } from '../../../types';
import { CtaButton } from './shared';
import { useClickOutside } from '../useClickOutside';

interface LocationInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  autoFocus?: boolean;
}

function LocationInput({ label, value, onChange, placeholder, autoFocus }: LocationInputProps) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useClickOutside(ref, () => setOpen(false));

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=8&featuretype=city`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        const results: string[] = Array.from(
          new Set(data.map((d: any) => d.display_name.split(',').slice(0, 2).join(',').trim()))
        );
        setSuggestions(results);
      } catch { setSuggestions([]); }
    }, 300);
  }, [value]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ fontSize: theme.fontSize.xs, fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.15em', color: theme.colors.muted, display: 'block', marginBottom: theme.spacing.sm }}>
        {label}
      </label>
      <input
        className="ghost-input"
        style={{ textAlign: 'left' }}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
          background: theme.colors.card,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.md,
          boxShadow: theme.shadows.card,
          maxHeight: 200, overflowY: 'auto',
          marginTop: 4,
        }}>
          {suggestions.map(loc => (
            <button key={loc} type="button"
              onMouseDown={() => { onChange(loc); setOpen(false); }}
              style={{
                width: '100%', padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                border: 'none', background: 'transparent', textAlign: 'left',
                fontSize: theme.fontSize.base, color: theme.colors.navy, cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = theme.colors.tealBg; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {loc}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface Props { form: FormData; onChange: (f: keyof FormData, v: unknown) => void; onNext: () => void; onBack?: () => void; }

export function StepDestination({ form, onChange, onNext, onBack }: Props) {
  const mobile = useIsMobile();
  const f = mobile ? theme.form.mobile : theme.form;
  const valid = form.destination.trim() && form.departure.trim();

  return (
    <div style={{ maxWidth: mobile ? '100%' : 'min(80%, 700px)', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.md, marginBottom: mobile ? theme.spacing['2xl'] : theme.spacing['4xl'] }}>
        <img src={connieSuitcase} alt="Connie" referrerPolicy="no-referrer"
          style={{ width: 'auto', height: mobile ? 'min(12vh, 100px)' : 'min(20vh, 250px)', objectFit: 'contain' }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        <div>
          <h2 style={{ fontFamily: theme.fontFamily.display, fontSize: f.headingSize, fontWeight: theme.fontWeight.light, color: theme.colors.navy, margin: 0, letterSpacing: '-0.02em', textAlign: 'center' }}>
            "Where shall we{' '}
            <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>go?</span>"
          </h2>
          <p style={{ color: theme.colors.textMuted, fontSize: f.subtitleSize, fontWeight: theme.fontWeight.light, margin: `${theme.spacing.xs} 0 0`, textAlign: 'center' }}>
            My intuition is powered by <strong style={{ color: theme.colors.teal }}>Natural Language-to-API Bridging</strong>, allowing me to instantly traverse global databases to find your perfect coordinates
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing['2xl'] }}>
        <LocationInput label="Departing From" value={form.departure} onChange={v => onChange('departure', v)} placeholder="From where?" autoFocus />
        <LocationInput label="Destination" value={form.destination} onChange={v => onChange('destination', v)} placeholder="To where?" />
      </div>

      <CtaButton label="Continue" onClick={onNext} disabled={!valid} onBack={onBack} />
    </div>
  );
}
